import React, { useCallback, useEffect, useReducer, useRef } from 'react'
import { DomainNotificationsProviderContext, domainNotificationsProviderReducer } from './internal'
import { NotificationDto } from '../../features/notifications'
import { useService } from '@pushly/aqe/lib/hooks'
import { NotificationService } from '../../services'
import { ApiVersion } from '../../enums/api-version.enum'
import { CustomApiResponseErrorHandler } from '../../types/api-call-options-with-custom-error-handler'
import { AbTestNotificationRole } from '@pushly/models/lib/enums/ab-test-notification-role'

type NotificationProviderProps = {
    domainId: number
    notificationId?: string | null
    notification?: NotificationDto
    testId?: string | null
    onFetchError?: CustomApiResponseErrorHandler
    children: React.ReactNode
}

export const DomainNotificationsProvider = (props: NotificationProviderProps) => {
    const { domainId, onFetchError, children } = props

    const propsNotificationId: string | undefined | null = 'notificationId' in props ? props.notificationId : undefined
    const propsTestId: number | undefined = props.testId ? Number(props.testId) : undefined

    const notifSvc = useService(NotificationService)

    const [state, dispatch] = useReducer(domainNotificationsProviderReducer, {
        domainId,
        loading: !!props.notificationId,
        notificationId: props.notification?.id?.toString() ?? props.notificationId ?? null,
        notifications: props.notification ? [props.notification] : [],
    })

    // prevents duplicate load effects without triggering re-renders
    const localLoaderInitializedRef = useRef(false)

    const loadRemoteNotification = useCallback(async () => {
        const canLoadRemote = !localLoaderInitializedRef.current || domainId !== state.domainId
        if (!canLoadRemote) return

        if (!propsNotificationId) return

        if (!localLoaderInitializedRef.current) localLoaderInitializedRef.current = true
        if (!state.loading) dispatch({ type: 'loading:set', loading: true })

        const notifications: NotificationDto[] = []

        const notification = await notifSvc.fetchNotificationById(domainId, propsNotificationId, {
            query: { includeSchedules: true },
            cancellationKey: `nl-n${propsNotificationId}`,
            version: ApiVersion.V4,
            errorHandler: onFetchError,
        })

        if (notification) {
            if (!propsTestId) {
                delete notification.abTest
                notifications.push(notification)
            } else {
                const {
                    notifications: _notifications,
                    schedules,
                    ...test
                } = (await notifSvc.fetchTestById(domainId, propsTestId, undefined, `nl-t${propsTestId}`)) as any

                const testNotifications = _notifications as any[]

                testNotifications
                    .map((n) => NotificationDto.parse(n))
                    .filter((notif) => {
                        // Exclude rollout/winner notification from variants when duplicating
                        return notif.abTest?.role !== AbTestNotificationRole.ROLLOUT
                    })
                    .forEach((notif) => {
                        notif.audience = notification?.audience! ?? {}
                        notif.abTest = {
                            ...(notif.abTest ?? {}),
                            ...test,
                        }

                        const schedule = schedules.find((s) => {
                            return s.notificationId.toString() === notif.id.toString()
                        })

                        if (schedule) {
                            notif.status = schedule.status ?? notif.status
                            notif.deliverySpec = {
                                ...schedule.deliverySpec,
                                ...notif.deliverySpec,
                            }
                        }

                        notifications.push(notif)
                    })
            }
        }

        if (notifications.length) {
            dispatch({ type: 'notifications:set', notifications })
        }

        dispatch({ type: 'loading:set', loading: false })
    }, [domainId, propsNotificationId, propsTestId])

    useEffect(() => {
        loadRemoteNotification()
    }, [loadRemoteNotification])

    return (
        <DomainNotificationsProviderContext.Provider value={{ state, dispatch }}>
            {children}
        </DomainNotificationsProviderContext.Provider>
    )
}
